// Layout helpers for the Schedule of Work PDF (jsPDF): A4 portrait in mm,
// Higgins brand colours (packages/ui/BRAND-COLOURS.md), Roboto when the
// document has it (pdf-fonts.ts), else Helvetica. A cursor moves down the page;
// anything that won't fit starts a new page.
import type { jsPDF } from 'jspdf';
import { HIGGINS_LOGO_ASPECT, HIGGINS_LOGO_PNG } from '@higgins/ui';
import { ROBOTO } from './pdf-fonts';

export const PAGE = { width: 210, height: 297, margin: 14 } as const;
export const CONTENT_WIDTH = PAGE.width - 2 * PAGE.margin;
/** Where content starts below the header, and must stop above the footer. */
const TOP = 24;
const BOTTOM = PAGE.height - 16;

export const BRAND = {
  orange: '#ff8300',
  purple: '#8b189b',
  charcoal: '#252729',
  tint: '#f1f2f2',
  mid: '#5c5f62',
  rule: '#d5d7d8',
} as const;

const LINE_MM_PER_PT = 0.42;
const HEADER_LOGO_HEIGHT = 9;

export class PdfPage {
  y = TOP;
  private readonly family: string;

  constructor(
    readonly doc: jsPDF,
    private readonly headerRight: string,
    /** The running header's title: the document's name (the Yearly Summaries share this kit, #515). */
    private readonly title = 'Schedule of Work',
  ) {
    this.family = ROBOTO in doc.getFontList() ? ROBOTO : 'helvetica';
  }

  /** Line height in mm for a font size in pt. */
  static lineHeight(size: number): number {
    return size * LINE_MM_PER_PT * 1.25;
  }

  font(size: number, style: 'normal' | 'bold' = 'normal', colour: string = BRAND.charcoal) {
    this.doc.setFont(this.family, style);
    this.doc.setFontSize(size);
    this.doc.setTextColor(colour);
  }

  /** Start a new page with the running header. */
  newPage() {
    this.doc.addPage();
    this.drawHeader();
    this.y = TOP;
  }

  /** Make room for `height` mm, starting a new page if it won't fit. */
  ensure(height: number) {
    if (this.y + height > BOTTOM) this.newPage();
  }

  drawHeader() {
    const { doc } = this;
    doc.setFillColor(BRAND.orange);
    doc.rect(0, 0, PAGE.width, 1.2, 'F');
    doc.addImage(HIGGINS_LOGO_PNG, 'PNG', PAGE.margin, 4, HEADER_LOGO_HEIGHT * HIGGINS_LOGO_ASPECT, HEADER_LOGO_HEIGHT);
    this.font(8, 'normal', BRAND.mid);
    doc.text(this.title, PAGE.width / 2, 10, { align: 'center' });
    doc.text(this.headerRight, PAGE.width - PAGE.margin, 10, { align: 'right' });
    doc.setDrawColor(BRAND.rule);
    doc.line(PAGE.margin, 16, PAGE.width - PAGE.margin, 16);
  }

  /** Footers on every page, once the page count is known: date · Page n of N · Higgins Coatings. */
  drawFooters(date: string) {
    const { doc } = this;
    const total = doc.getNumberOfPages();
    for (let n = 1; n <= total; n++) {
      doc.setPage(n);
      this.font(7.5, 'normal', BRAND.mid);
      const y = PAGE.height - 8;
      doc.text(date, PAGE.margin, y);
      doc.text(`Page ${n} of ${total}`, PAGE.width / 2, y, { align: 'center' });
      doc.text('Higgins Coatings', PAGE.width - PAGE.margin, y, { align: 'right' });
    }
  }

  /** Wrapped text at the cursor; breaks across pages line by line. */
  paragraph(text: string, options: { size?: number; style?: 'normal' | 'bold'; colour?: string; x?: number; width?: number; gapAfter?: number } = {}) {
    const { size = 9.5, style = 'normal', colour = BRAND.charcoal, x = PAGE.margin, width = CONTENT_WIDTH, gapAfter = 2 } = options;
    this.font(size, style, colour);
    const lh = PdfPage.lineHeight(size);
    for (const line of this.doc.splitTextToSize(text, width) as string[]) {
      this.ensure(lh);
      this.doc.text(line, x, this.y + lh * 0.8);
      this.y += lh;
    }
    this.y += gapAfter;
  }

  /** One bulleted line, indented. */
  bullet(text: string, size = 9) {
    this.paragraph(`•  ${text}`, { size, x: PAGE.margin + 3, width: CONTENT_WIDTH - 3, gapAfter: 0.3 });
  }

  /** A section heading on a coloured bar. */
  bar(label: string, colour: string = BRAND.charcoal) {
    this.ensure(14);
    this.doc.setFillColor(colour);
    this.doc.rect(PAGE.margin, this.y, CONTENT_WIDTH, 7, 'F');
    this.font(9.5, 'bold', '#ffffff');
    this.doc.text(label, PAGE.margin + 2.5, this.y + 4.8);
    this.y += 10;
  }

  /** A labelled note on the tint, with a coloured edge: scope notes, exclusions, defects. */
  noteBox(label: string, text: string, edge: string) {
    const size = 9;
    const lh = PdfPage.lineHeight(size);
    this.font(size);
    const lines = this.doc.splitTextToSize(text, CONTENT_WIDTH - 8) as string[];
    const height = 7 + lines.length * lh + 2;
    this.ensure(Math.min(height, 40));
    const top = this.y;
    this.doc.setFillColor(BRAND.tint);
    this.doc.rect(PAGE.margin, top, CONTENT_WIDTH, height, 'F');
    this.doc.setFillColor(edge);
    this.doc.rect(PAGE.margin, top, 1.2, height, 'F');
    this.font(8, 'bold', edge);
    this.doc.text(label.toUpperCase(), PAGE.margin + 4, top + 5);
    this.y = top + 7;
    this.paragraph(text, { size, x: PAGE.margin + 4, width: CONTENT_WIDTH - 8, gapAfter: 0 });
    this.y = Math.max(this.y, top + height) + 3;
  }
}

export interface Column {
  label: string;
  width: number;
  align?: 'left' | 'right';
}

/** A table: header row, then rows; bold rows for totals; repeats the header after a page break. */
export function table(page: PdfPage, columns: Column[], rows: { cells: string[]; bold?: boolean; shade?: boolean }[], x = PAGE.margin) {
  const { doc } = page;
  const rowHeight = 5.6;
  const header = () => {
    page.ensure(rowHeight * 2);
    doc.setFillColor(BRAND.charcoal);
    doc.rect(x, page.y, columns.reduce((w, c) => w + c.width, 0), rowHeight, 'F');
    page.font(8, 'bold', '#ffffff');
    let cx = x;
    for (const c of columns) {
      doc.text(c.label, c.align === 'right' ? cx + c.width - 1.5 : cx + 1.5, page.y + 3.9, { align: c.align === 'right' ? 'right' : 'left' });
      cx += c.width;
    }
    page.y += rowHeight;
  };
  header();
  for (const row of rows) {
    if (page.y + rowHeight > BOTTOM) {
      page.newPage();
      header();
    }
    if (row.shade) {
      doc.setFillColor(BRAND.tint);
      doc.rect(x, page.y, columns.reduce((w, c) => w + c.width, 0), rowHeight, 'F');
    }
    page.font(8.5, row.bold ? 'bold' : 'normal');
    let cx = x;
    row.cells.forEach((cell, i) => {
      const c = columns[i];
      const fitted = (doc.splitTextToSize(cell, c.width - 3) as string[])[0] ?? '';
      doc.text(fitted, c.align === 'right' ? cx + c.width - 1.5 : cx + 1.5, page.y + 3.9, { align: c.align === 'right' ? 'right' : 'left' });
      cx += c.width;
    });
    doc.setDrawColor(BRAND.rule);
    doc.line(x, page.y + rowHeight, cx, page.y + rowHeight);
    page.y += rowHeight;
  }
  page.y += 3;
}
